import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useRef } from 'react';
import {
  BuildingOfficeIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ArrowLeftIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { craApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';

const ACTIVITY_TYPES = ['LOAN', 'INVESTMENT', 'GRANT', 'DONATION', 'SERVICE'];
const PURPOSES = ['AFFORDABLE_HOUSING', 'COMMUNITY_SERVICES', 'ECONOMIC_DEVELOPMENT', 'REVITALIZATION_STABILIZATION', 'OTHER'];
const STATUSES = ['ACTIVE', 'COMPLETED', 'EXPIRED', 'PENDING_REVIEW'];

export default function CraCdActivitiesPage() {
  const { teamId, isLoading: teamLoading } = useTeamId();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<any>(null);
  const [typeFilter, setTypeFilter] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['cra-cd-activities', teamId, typeFilter],
    queryFn: async () => {
      const params: any = {};
      if (typeFilter) params.activityType = typeFilter;
      const res = await craApi.listCdActivities(teamId!, params);
      return res.data;
    },
    enabled: !!teamId,
  });

  const { data: summaryData } = useQuery({
    queryKey: ['cra-cd-summary', teamId],
    queryFn: async () => {
      const res = await craApi.getCdSummary(teamId!);
      return res.data;
    },
    enabled: !!teamId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => craApi.deleteCdActivity(teamId!, id),
    onSuccess: () => {
      toast.success('Activity deleted');
      queryClient.invalidateQueries({ queryKey: ['cra-cd-activities'] });
      queryClient.invalidateQueries({ queryKey: ['cra-cd-summary'] });
    },
    onError: () => toast.error('Failed to delete'),
  });

  const importMutation = useMutation({
    mutationFn: (file: File) => craApi.importCdActivities(teamId!, file),
    onSuccess: (res) => {
      const { imported, skipped, errors } = res.data;
      toast.success(`Imported ${imported} activities${skipped ? `, ${skipped} skipped` : ''}${errors?.length ? `, ${errors.length} errors` : ''}`);
      queryClient.invalidateQueries({ queryKey: ['cra-cd-activities'] });
      queryClient.invalidateQueries({ queryKey: ['cra-cd-summary'] });
    },
    onError: () => toast.error('CSV import failed'),
  });

  if (teamLoading || isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        <div className="h-96 bg-gray-200 rounded-lg" />
      </div>
    );
  }

  const activities = data?.activities || [];
  const summary = summaryData?.summary;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);

  return (
    <div>
      <div className="mb-6">
        <Link to="/cra" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to CRA Dashboard
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-navy-900">Community Development Activities</h1>
            <p className="mt-1 text-sm text-gray-500">Track loans; investments, grants, and donations; and services</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) importMutation.mutate(file);
                e.target.value = '';
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importMutation.isPending}
              className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowUpTrayIcon className="h-4 w-4" />
              {importMutation.isPending ? 'Importing...' : 'Import CSV'}
            </button>
            {/* Hide header CTA when list is empty — the empty-state shows
                its own "Add First Activity" button (matches Assessment Areas). */}
            {activities.length > 0 && (
              <button
                onClick={() => { setEditingActivity(null); setModalOpen(true); }}
                className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium bg-ocean-600 text-white hover:bg-ocean-700"
              >
                <PlusIcon className="h-4 w-4" />
                Add Activity
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {ACTIVITY_TYPES.map(type => (
            <div key={type} className="bg-white rounded-lg border border-gray-100 shadow-sm p-4">
              <p className="text-xs text-gray-500 uppercase">{type}S</p>
              <p className="text-xl font-semibold text-navy-900 tabular-nums">{summary.byType?.[type] ?? 0}</p>
              {summary.amountByType?.[type] && (
                <p className="text-xs text-gray-500 mt-1">{formatCurrency(summary.amountByType[type])}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      <div className="mb-4">
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-ocean-500 focus:outline-none"
        >
          <option value="">All Types</option>
          {ACTIVITY_TYPES.map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      {/* Activities Table */}
      <div className="bg-white rounded-lg shadow">
        {activities.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Purpose</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">LMI</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {activities.map((activity: any) => (
                  <tr key={activity.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{activity.title}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700">
                        {activity.activityType}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{(activity.purpose || '').replace(/_/g, ' ')}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{activity.organizationName}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 tabular-nums">{activity.amount ? formatCurrency(activity.amount) : '—'}</td>
                    <td className="px-6 py-4">
                      {activity.benefitsLmiTracts ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">Yes</span>
                      ) : (
                        <span className="text-xs text-gray-400">No</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        activity.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                        activity.status === 'COMPLETED' ? 'bg-blue-100 text-blue-700' :
                        activity.status === 'EXPIRED' ? 'bg-gray-100 text-gray-600' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {activity.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => { setEditingActivity(activity); setModalOpen(true); }}
                          className="p-1.5 text-gray-400 hover:text-ocean-600"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm('Delete this activity?')) deleteMutation.mutate(activity.id);
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-16 text-center">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-base font-semibold text-navy-900">No CD Activities</h3>
            <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">
              Track community development loans; qualified investments, grants, and donations; and community development services that qualify for CRA credit.
            </p>
            <button
              onClick={() => { setEditingActivity(null); setModalOpen(true); }}
              className="mt-4 inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium bg-ocean-600 text-white hover:bg-ocean-700"
            >
              <PlusIcon className="h-4 w-4" />
              Add First Activity
            </button>
          </div>
        )}
      </div>

      {modalOpen && (
        <CdActivityModal
          teamId={teamId!}
          activity={editingActivity}
          onClose={() => { setModalOpen(false); setEditingActivity(null); }}
        />
      )}
    </div>
  );
}

function CdActivityModal({ teamId, activity, onClose }: { teamId: string; activity?: any; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    title: activity?.title || '',
    description: activity?.description || '',
    activityType: activity?.activityType || 'LOAN',
    purpose: activity?.purpose || 'AFFORDABLE_HOUSING',
    organizationName: activity?.organizationName || '',
    amount: activity?.amount?.toString() || '',
    commitmentDate: activity?.commitmentDate ? new Date(activity.commitmentDate).toISOString().split('T')[0] : '',
    censusTract: activity?.censusTract || '',
    benefitsLmiTracts: activity?.benefitsLmiTracts ?? true,
    status: activity?.status || 'ACTIVE',
  });

  const mutation = useMutation({
    mutationFn: (data: any) =>
      activity
        ? craApi.updateCdActivity(teamId, activity.id, data)
        : craApi.createCdActivity(teamId, data),
    onSuccess: () => {
      toast.success(activity ? 'Activity updated' : 'Activity created');
      queryClient.invalidateQueries({ queryKey: ['cra-cd-activities'] });
      queryClient.invalidateQueries({ queryKey: ['cra-cd-summary'] });
      onClose();
    },
    onError: () => toast.error(activity ? 'Failed to update' : 'Failed to create'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      ...formData,
      amount: formData.amount ? parseFloat(formData.amount) : null,
      commitmentDate: formData.commitmentDate || undefined,
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {activity ? 'Edit CD Activity' : 'New CD Activity'}
          </h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input name="title" type="text" value={formData.title} onChange={handleChange} required
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select name="activityType" value={formData.activityType} onChange={handleChange}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500">
                {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
              <select name="purpose" value={formData.purpose} onChange={handleChange}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500">
                {PURPOSES.map(p => <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
            <input name="organizationName" type="text" value={formData.organizationName} onChange={handleChange}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea name="description" value={formData.description} onChange={handleChange} rows={2}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
              <input name="amount" type="number" value={formData.amount} onChange={handleChange}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Commitment Date</label>
              <input name="commitmentDate" type="date" value={formData.commitmentDate} onChange={handleChange}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Census Tract</label>
              <input name="censusTract" type="text" value={formData.censusTract} onChange={handleChange} placeholder="e.g., 0127.00"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select name="status" value={formData.status} onChange={handleChange}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500">
                {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="benefitsLmi" name="benefitsLmiTracts" checked={formData.benefitsLmiTracts}
              onChange={e => setFormData(prev => ({ ...prev, benefitsLmiTracts: e.target.checked }))}
              className="rounded border-gray-300 text-ocean-600 focus:ring-ocean-500" />
            <label htmlFor="benefitsLmi" className="text-sm text-gray-700">Benefits LMI tracts</label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={mutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-ocean-600 rounded-md hover:bg-ocean-700 disabled:opacity-50">
              {mutation.isPending ? 'Saving...' : activity ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
